/**
 * Mapping between the public pages and their per-language URLs.
 *
 * Spanish and Portuguese live at /es and /pt rather than behind an
 * Accept-Language swap, so picking a language has to move the reader as well as
 * re-render them — otherwise the choice is lost on the next reload, and the URL
 * disagrees with what search engines indexed.
 */

export type PublicLang = 'en' | 'es' | 'pt';

/**
 * Public pages that exist in every language. Everything else — /terms,
 * /coming-soon, the static /privacy — has a single version, and App.tsx
 * bounces unknown paths to /welcome, so a blind /pt prefix would strand people.
 */
const TRANSLATED = ['', '/about', '/pricing'];

const PREFIX: Record<PublicLang, string> = { en: '', es: '/es', pt: '/pt' };

/**
 * The part of a public path after its language prefix — '' for any home page,
 * since /, /welcome, /es and /pt are all the landing page.
 */
export function basePath(pathname: string): string {
  const withoutPrefix = pathname.replace(/^\/(es|pt)(?=\/|$)/, '');
  const clean = withoutPrefix.replace(/\/+$/, '');
  return clean === '/welcome' ? '' : clean;
}

/**
 * Where the language switcher should send someone who picks `lang`, or null
 * when this page has no counterpart in that language and should just be
 * re-rendered where it stands.
 */
export function pathInLanguage(pathname: string, lang: PublicLang): string | null {
  const base = basePath(pathname);
  if (!TRANSLATED.includes(base)) return null;
  return `${PREFIX[lang]}${base}` || '/';
}
